import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import './DataAnalysis.css';

const MotorQuery = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hp, setHp] = useState("");
    const [filtered, setFiltered] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [errorMsg, setErrorMsg] = useState(null);

    // 判斷是否手機
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 讀取資料
    useEffect(() => {
        loadMotorData();
    }, []);

    const loadMotorData = async () => {
        try {
            setLoading(true);
            setErrorMsg(null);
            console.log("正在載入馬達資料 from motor_3phase_220v...");

            const { data, error } = await supabase
                .from("motor_3phase_220v")
                .select("*")
                .order("horsepower", { ascending: true });

            if (error) {
                console.error('載入馬達資料失敗 API Error:', error);
                throw error;
            }

            if (!data || data.length === 0) {
                console.warn("未找到馬達資料 (data is empty)");
                setErrorMsg("查詢成功，但資料庫中沒有資料 (Table is empty or RLS policy restricts access)");
            }

            setData(data || []);
        } catch (error) {
            console.error('載入馬達資料失敗:', error);
            setErrorMsg(`讀取失敗: ${error.message || '未知錯誤'} (Code: ${error.code})`);
        } finally {
            setLoading(false);
        }
    };

    // 篩選功能
    useEffect(() => {
        if (hp === "") {
            setFiltered(data);
        } else {
            setFiltered(
                data.filter((item) =>
                    item.horsepower.toString().includes(hp.toString())
                )
            );
        }
    }, [hp, data]);

    if (loading) {
        return (
            <div className="data-analysis">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>正在載入馬達資料...</p>
                </div>
            </div>
        );
    }

    if (errorMsg) {
        return (
            <div className="data-analysis">
                <div className="error-container" style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                    <h3>⚠️ 發生錯誤</h3>
                    <p>{errorMsg}</p>
                    <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                        請檢查 Supabase 資料庫中的 <code>motor_3phase_220v</code> 資料表是否存在，以及 RLS (Row Level Security) 設定是否允許讀取。
                    </p>
                    <button className="refresh-btn" onClick={loadMotorData} style={{ marginTop: '15px' }}>
                        重試
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pvc-pipes-page">
            <div className="page-header">
                <h2>三相馬達資料查詢</h2>
                <p>(220V 三相馬達規格表)</p>
            </div>

            {/* 搜尋控制區域 */}
            <div className="search-controls">
                <div className="search-input-container">
                    <input
                        type="text"
                        placeholder="搜尋馬力 (例如：5 或 10)"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {/* 結果統計 */}
            <div className="results-info">
                <p>找到 {filtered.length} 個結果 (共 {data.length} 種馬達)</p>
            </div>

            {/* 資料顯示 */}
            {isMobile ? (
                // 手機卡片模式
                <div className="pipes-grid-mobile">
                    {filtered.map((item) => (
                        <div key={item.id} className="pipe-card">
                            <div className="pipe-card-header">
                                <h3>馬力：{item.horsepower} HP</h3>


                            </div>
                            <div className="pipe-card-content">
                                {/* 基本資訊 */}
                                <div className="motor-section">
                                    <div className="pipe-spec">
                                        <span className="spec-label">電動機容量(KW)：</span>
                                        <span className="spec-value">{item.kw || 'N/A'} kW</span>
                                    </div>
                                    <div className="pipe-spec highlight-current">
                                        <span className="spec-label">滿載電流(A)：</span>
                                        <span className="spec-value">{item.full_load_current || 'N/A'} A</span>
                                    </div>
                                </div>

                                {/* 線徑 */}
                                <div className="motor-section">
                                    <div className="section-title">最小線徑(銅)</div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">線徑大小(mm²或平方)：</span>
                                        <span className="spec-value">{item.wire_diameter || '-'} mm²</span>
                                    </div>
                                </div>

                                {/* 無熔絲開關 */}
                                <div className="motor-section">
                                    <div className="section-title">無熔絲開關(NFB)</div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">框架容量 AF：</span>
                                        <span className="spec-value">{item.nfb_frame_capacity || 'N/A'}</span>
                                    </div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">跳脫電流選定值 AT：</span>
                                        <span className="spec-value">{item.nfb_trip_current || 'N/A'}</span>
                                    </div>
                                </div>

                                {/* 刀型開關 */}
                                <div className="motor-section">
                                    <div className="section-title">刀型開關</div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">額定電流 A：</span>
                                        <span className="spec-value">{item.knife_switch_rated_current || 'N/A'}</span>
                                    </div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">熔絲容量 A：</span>
                                        <span className="spec-value">{item.knife_switch_fuse_capacity || 'N/A'}</span>
                                    </div>
                                </div>

                                {/* 電磁開關 */}
                                <div className="motor-section">
                                    <div className="section-title">電磁開關</div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">額定馬力 HP：</span>
                                        <span className="spec-value">{item.magnetic_switch_hp || 'N/A'}</span>
                                    </div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">過載電流選定值 A：</span>
                                        <span className="spec-value">{item.magnetic_switch_trip_current || 'N/A'}</span>
                                    </div>
                                </div>

                                {/* 安培表 */}
                                <div className="motor-section">
                                    <div className="section-title">安培表(電流表)</div>
                                    <div className="pipe-spec">
                                        <span className="spec-label">標示值 A：</span>
                                        <span className="spec-value">{item.ammeter_value || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                // 桌機表格模式
                <div className="pipes-table-container">
                    <table className="pipes-table">
                        <thead>
                            <tr>
                                <th>馬力</th>
                                <th>kW</th>
                                <th>滿載電流(A)</th>
                                <th>線徑(mm²)</th>
                                <th>NFB(AF/AT)</th>
                                <th>刀型(A/熔絲)</th>
                                <th>電磁(HP/AT)</th>
                                <th>安培表(A)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((item) => (
                                <tr key={item.id}>
                                    <td className="pipe-inch">{item.horsepower}</td>
                                    <td className="pipe-code">{item.kw}</td>
                                    <td>{item.full_load_current}</td>
                                    <td>{item.wire_diameter || "-"}</td>
                                    <td>{item.nfb_frame_capacity}/{item.nfb_trip_current}</td>
                                    <td>{item.knife_switch_rated_current}/{item.knife_switch_fuse_capacity}</td>
                                    <td>{item.magnetic_switch_hp}/{item.magnetic_switch_trip_current}</td>
                                    <td>{item.ammeter_value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </div>
    );
};

export default MotorQuery;
